import { useTranslation } from "react-i18next";
import { Film, Image as ImageIcon, MapPin, Music, Palette, Play, Users } from "lucide-react";

const PANEL = [
  { Icon: Film, label: "landing.panelScene", meta: "06", active: true },
  { Icon: ImageIcon, label: "landing.panelPrompt" },
  { Icon: Users, label: "landing.panelCharacters" },
  { Icon: MapPin, label: "landing.panelPlaces" },
  { Icon: Palette, label: "landing.panelStyle" },
  { Icon: Music, label: "landing.panelAudio" },
];

const WAVE = [3, 6, 9, 14, 8, 12, 18, 10, 6, 13, 17, 9, 5, 11, 15, 7, 4, 10, 16, 12, 6, 9, 14, 8];

// Decorative studio monitor (CSS only): bezel, HUD, generated landscape, timeline, side panel, mirrored floor reflection.
export function HeroDisplay() {
  const { t } = useTranslation();
  return (
    <div data-testid="landing-hero-visual" className="group relative">
      <div aria-hidden className="absolute -inset-16 rounded-[60px] bg-[radial-gradient(55%_55%_at_50%_45%,rgba(46,123,255,0.36),transparent_70%)] blur-3xl transition-opacity duration-700 group-hover:opacity-100 opacity-90" />
      <div aria-hidden className="absolute -inset-6 rounded-[36px] bg-[radial-gradient(60%_60%_at_60%_40%,rgba(22,196,230,0.16),transparent_70%)] blur-2xl" />

      {/* tech corner markers outside the bezel */}
      {["-left-3 -top-3 border-l border-t", "-right-3 -top-3 border-r border-t", "-left-3 -bottom-3 border-l border-b", "-right-3 -bottom-3 border-r border-b"].map((c) => (
        <span key={c} aria-hidden className={`absolute h-6 w-6 border-[#7FD4FF]/70 ${c}`} />
      ))}

      <div className="animate-float-slow relative">
        <div className="reflect-floor relative rounded-2xl border border-[#4d8fff]/60 bg-[#070B14] p-[7px] shadow-[0_0_0_1px_rgba(46,123,255,0.28),0_0_70px_-8px_rgba(46,123,255,0.8),0_50px_110px_-40px_rgba(0,0,0,0.95),inset_0_1px_0_rgba(255,255,255,0.08)] transition-transform duration-700 ease-out group-hover:-translate-y-1">
          <div aria-hidden className="absolute inset-x-12 -top-px h-px bg-gradient-to-r from-transparent via-[#9CC8FF] to-transparent" />
          <div className="relative aspect-[16/9.4] overflow-hidden rounded-xl bg-[#040710]">
            {/* HUD top bar */}
            <div className="absolute inset-x-0 top-0 z-20 flex h-8 items-center justify-between border-b border-[#2E7BFF]/20 bg-[#05070D]/85 px-3 text-[10px] tracking-[0.18em] text-muted-foreground backdrop-blur">
              <span className="flex items-center gap-2">
                <img src="/brand/logo-simulation-studios.jpeg" alt="" className="logo-neon h-4 w-4 object-contain" />
                <span className="font-display font-semibold uppercase text-foreground/80">Project_001</span>
                <span className="hidden text-[#7FD4FF]/70 sm:inline">· 9:16 · 30 fps</span>
              </span>
              <span className="flex items-center gap-2 font-display font-bold">
                <span className="rounded border border-[#2E7BFF]/40 px-1.5 py-px text-[#7FD4FF]">4K</span>
                <span className="animate-pulse-soft h-1.5 w-1.5 rounded-full bg-[#FF5C6C] shadow-[0_0_8px_#FF5C6C]" />
                <span className="uppercase text-[#FF5C6C]">Rec</span>
              </span>
            </div>

            {/* thumbnails strip */}
            <div className="absolute bottom-9 left-3 top-11 z-10 hidden w-[14%] flex-col gap-1.5 sm:flex">
              {[0, 1, 2, 3].map((i) => (
                <div
                  key={i}
                  className={`relative flex-1 overflow-hidden rounded-md border ${i === 0 ? "border-[#7FD4FF]/80 shadow-[0_0_14px_-4px_rgba(127,212,255,0.9)]" : "border-[#2E7BFF]/25 opacity-70"}`}
                  style={{ background: `linear-gradient(${150 + i * 30}deg, rgba(60,120,255,0.55), rgba(8,14,28,0.95) 55%, rgba(255,140,60,${0.25 - i * 0.05}))` }}
                >
                  <span className="absolute bottom-1 left-1 font-display text-[8px] font-bold tracking-widest text-white/70">0{i + 1}</span>
                </div>
              ))}
            </div>

            {/* main screen: generated landscape */}
            <div className="absolute bottom-9 left-3 right-3 top-11 overflow-hidden rounded-lg border border-[#2E7BFF]/35 sm:left-[17%] sm:right-[29%]">
              <div aria-hidden className="absolute inset-0 bg-[linear-gradient(180deg,#0a1636_0%,#12264f_38%,#3a3f6a_52%,#0a1020_62%,#05070d_100%)]" />
              {/* sun bloom */}
              <div aria-hidden className="absolute left-1/2 top-[46%] h-24 w-24 -translate-x-1/2 -translate-y-1/2 rounded-full bg-[radial-gradient(circle,rgba(255,196,120,0.95),rgba(255,140,60,0.5)_35%,transparent_70%)] blur-[2px]" />
              <div aria-hidden className="absolute left-1/2 top-[46%] h-56 w-56 -translate-x-1/2 -translate-y-1/2 rounded-full bg-[radial-gradient(circle,rgba(255,160,90,0.35),transparent_65%)] blur-xl" />
              {/* mountains (3 layers) */}
              <div aria-hidden className="absolute inset-x-[-4%] top-[36%] h-[34%] bg-[#0d1a3a] [clip-path:polygon(0%_100%,5%_72%,12%_84%,20%_56%,27%_70%,35%_40%,41%_62%,48%_48%,55%_68%,62%_30%,69%_58%,76%_44%,84%_66%,91%_50%,100%_100%)] opacity-80" />
              <div aria-hidden className="absolute inset-x-[-4%] top-[44%] h-[30%] bg-[#0a1230] [clip-path:polygon(0%_100%,8%_78%,15%_62%,23%_82%,31%_54%,39%_74%,46%_66%,54%_42%,60%_72%,68%_58%,75%_80%,83%_50%,90%_70%,100%_100%)]" />
              <div aria-hidden className="absolute inset-x-[-4%] top-[52%] h-[26%] bg-[#05080f] [clip-path:polygon(0%_100%,6%_84%,14%_70%,22%_88%,30%_66%,40%_80%,50%_60%,58%_78%,66%_64%,74%_86%,82%_70%,92%_82%,100%_100%)]" />
              {/* mist */}
              <div aria-hidden className="absolute inset-x-0 top-[56%] h-[14%] bg-[linear-gradient(180deg,transparent,rgba(120,160,255,0.18),transparent)] blur-md" />
              {/* floor grid */}
              <div aria-hidden className="absolute inset-x-0 bottom-0 h-[40%] [background-image:linear-gradient(rgba(22,196,230,0.45)_1px,transparent_1px),linear-gradient(90deg,rgba(22,196,230,0.45)_1px,transparent_1px)] [background-size:28px_24px] [transform:perspective(240px)_rotateX(60deg)] [transform-origin:50%_0] opacity-70 [mask-image:linear-gradient(180deg,rgba(0,0,0,0.9),transparent)]" />
              <div aria-hidden className="absolute inset-x-0 top-[60%] h-px bg-gradient-to-r from-transparent via-[#9CC8FF] to-transparent shadow-[0_0_16px_#2E7BFF]" />
              {/* figure silhouette */}
              <div aria-hidden className="absolute left-[46%] top-[47%] h-[16%] w-[4%] bg-[#04060c] [clip-path:polygon(38%_0,62%_0,66%_18%,80%_28%,82%_55%,70%_58%,72%_100%,28%_100%,30%_58%,18%_55%,20%_28%,34%_18%)]" />
              {/* play */}
              <div className="absolute left-1/2 top-1/2 grid h-12 w-12 -translate-x-1/2 -translate-y-1/2 place-items-center rounded-full border border-white/50 bg-white/10 backdrop-blur-md shadow-[0_0_34px_-2px_rgba(156,200,255,0.9)] transition-transform duration-500 group-hover:scale-110">
                <Play className="ml-0.5 h-5 w-5 fill-white text-white" />
              </div>
              {/* HUD chips */}
              <span className="absolute left-2 top-2 rounded border border-white/15 bg-black/30 px-1.5 py-px font-display text-[9px] font-bold tracking-[0.2em] text-white/80 backdrop-blur">SCENE 01 / 06</span>
              <span className="absolute right-2 top-2 font-display text-[9px] font-bold tracking-[0.2em] text-[#7FD4FF]">00:00:12:04</span>
              <span className="absolute bottom-2 right-2 font-display text-[9px] font-semibold uppercase tracking-[0.32em] text-white/80">A greater tomorrow</span>
              <div aria-hidden className="absolute inset-0 bg-[linear-gradient(180deg,rgba(255,255,255,0.06),transparent_28%)]" />
            </div>

            {/* side panel */}
            <div className="absolute bottom-9 right-3 top-11 z-10 hidden w-[26%] flex-col gap-1.5 sm:flex">
              {PANEL.map(({ Icon, label, meta, active }) => (
                <div
                  key={label}
                  className={
                    "flex flex-1 items-center gap-2 rounded-md border px-2.5 text-[10px] font-medium tracking-wide " +
                    (active
                      ? "border-[#4d8fff]/70 bg-[#2E7BFF]/15 text-foreground shadow-[inset_0_0_20px_-10px_rgba(46,123,255,0.9),0_0_16px_-8px_rgba(46,123,255,0.8)]"
                      : "border-[#2E7BFF]/15 bg-[#0A1020]/80 text-muted-foreground")
                  }
                >
                  <Icon className={"h-3 w-3 " + (active ? "text-[#7FD4FF]" : "text-[#2E7BFF]/80")} />
                  <span className="truncate">{t(label)}</span>
                  {meta && <span className="ml-auto font-display text-[9px] text-[#7FD4FF]/80">/ {meta}</span>}
                </div>
              ))}
            </div>

            {/* timeline */}
            <div className="absolute inset-x-3 bottom-2 z-10 flex h-6 items-center gap-2">
              <div className="flex h-full flex-1 items-end gap-[3px] overflow-hidden rounded-md border border-[#2E7BFF]/20 bg-[#0A1020]/80 px-2 pb-1">
                {WAVE.map((h, i) => (
                  <span key={i} className="w-[3px] rounded-sm bg-gradient-to-t from-[#2E7BFF] to-[#16C4E6]" style={{ height: `${h}px`, opacity: i < 16 ? 0.9 : 0.35 }} />
                ))}
                <span className="animate-scrub pointer-events-none absolute left-2 top-0 h-full border-r border-[#9CC8FF] shadow-[0_0_10px_#7FD4FF]" style={{ width: "62%" }} />
              </div>
              <span className="font-display text-[9px] font-bold tracking-[0.18em] text-[#7FD4FF]">RENDER 68%</span>
            </div>
          </div>
        </div>
      </div>

      <div aria-hidden className="pointer-events-none absolute inset-x-4 -bottom-10 h-px bg-gradient-to-r from-transparent via-[#4d8fff]/70 to-transparent shadow-[0_0_24px_3px_rgba(46,123,255,0.45)]" />
    </div>
  );
}
